import React from "react";
import { cn } from "@/lib/utils";
import type { DropZonePosition } from "./drop-zone-overlay";

interface SplitIndicatorProps {
	position: DropZonePosition;
	isVisible: boolean;
	containerRect?: DOMRect;
}

export const SplitIndicator: React.FC<SplitIndicatorProps> = ({
	position,
	isVisible,
	containerRect,
}) => {
	if (!isVisible || !containerRect || position === "center") return null;

	const getIndicatorStyles = (): React.CSSProperties => {
		const { top, left, width, height } = containerRect;
		const indicatorSize = 4;
		const offset = 0;

		switch (position) {
			case "top":
				return {
					position: "fixed",
					top: top + offset,
					left: left,
					width: width,
					height: indicatorSize,
					transform: "translateY(-50%)",
				};
			case "bottom":
				return {
					position: "fixed",
					top: top + height - offset,
					left: left,
					width: width,
					height: indicatorSize,
					transform: "translateY(-50%)",
				};
			case "left":
				return {
					position: "fixed",
					top: top,
					left: left + offset,
					width: indicatorSize,
					height: height,
					transform: "translateX(-50%)",
				};
			case "right":
				return {
					position: "fixed",
					top: top,
					left: left + width - offset,
					width: indicatorSize,
					height: height,
					transform: "translateX(-50%)",
				};
			default:
				return {};
		}
	};

	return (
		<div
			className={cn(
				"pointer-events-none z-[100] bg-primary shadow-lg transition-all duration-200",
				isVisible ? "opacity-100" : "opacity-0",
			)}
			style={getIndicatorStyles()}
		>
			<div className="absolute inset-0 animate-pulse bg-primary/50" />
		</div>
	);
};

interface SplitPreviewProps {
	position: DropZonePosition;
	isVisible: boolean;
	containerRect?: DOMRect;
}

export const SplitPreview: React.FC<SplitPreviewProps> = ({
	position,
	isVisible,
	containerRect,
}) => {
	if (!isVisible || !containerRect) return null;

	const getPreviewStyles = (): React.CSSProperties => {
		const { top, left, width, height } = containerRect;

		switch (position) {
			case "top":
				return {
					position: "fixed",
					top: top,
					left: left,
					width: width,
					height: height / 2,
					backgroundColor: "hsl(var(--primary) / 0.1)",
					border: "2px dashed hsl(var(--primary) / 0.3)",
				};
			case "bottom":
				return {
					position: "fixed",
					top: top + height / 2,
					left: left,
					width: width,
					height: height / 2,
					backgroundColor: "hsl(var(--primary) / 0.1)",
					border: "2px dashed hsl(var(--primary) / 0.3)",
				};
			case "left":
				return {
					position: "fixed",
					top: top,
					left: left,
					width: width / 2,
					height: height,
					backgroundColor: "hsl(var(--primary) / 0.1)",
					border: "2px dashed hsl(var(--primary) / 0.3)",
				};
			case "right":
				return {
					position: "fixed",
					top: top,
					left: left + width / 2,
					width: width / 2,
					height: height,
					backgroundColor: "hsl(var(--primary) / 0.1)",
					border: "2px dashed hsl(var(--primary) / 0.3)",
				};
			case "center":
				return {
					position: "fixed",
					top: top,
					left: left,
					width: width,
					height: height,
					backgroundColor: "hsl(var(--accent) / 0.1)",
					border: "2px solid hsl(var(--accent) / 0.3)",
				};
			default:
				return {};
		}
	};

	return (
		<div
			className={cn(
				"pointer-events-none z-[99] transition-all duration-200",
				isVisible ? "opacity-100" : "opacity-0",
			)}
			style={getPreviewStyles()}
		/>
	);
};